import React, {useState, useEffect} from "react";
import {getLogLeveLIndicatorColor} from "./utils";
import {format} from "date-fns";
import {customTheme2} from "../../K8sInfo/YamlViewer";
import SyntaxHighlighter from "react-syntax-highlighter";
import {useFeatureFlagsStore} from "../../../store/featureFlagsStore";
import {Bot, Copy, Info} from "lucide-react";
import { dashboardJsonReplacer } from "components/Dashboarding/Dashboard";


function LogHeader(props: {
    logLevel: string,
    datetime: number,
    logMessage?: string,
    logAttributes?: Record<string, any>
}) {
    const [guardianEnabled, setGuardianEnabled] = useState(false);
    const featureFlags = useFeatureFlagsStore();
    
    useEffect(() => {
        featureFlags.featureFlagEnabled("guardian.chat").then((enabled) => {
            setGuardianEnabled(enabled);
        });
    }, []);

    const logLevelColor = getLogLeveLIndicatorColor(props.logLevel);
    
    const handleSendToGuardian = () => {
        // Format log data for Guardian
        const formattedDateTime = format(new Date(props.datetime), "PPPppp");
        const logLevel = props.logLevel.toUpperCase();
        
        // Build a message with all log information
        let message = `Log Level: ${logLevel}\nTime: ${formattedDateTime}\n`;
        
        if (props.logMessage) {
            message += `\nLog Message:\n\`\`\`\n${props.logMessage}\n\`\`\``;
        }
        
        if (props.logAttributes) {
            // @ts-ignore
            message += `\n\nLog Attributes:\n\`\`\`json\n${JSON.stringify(Object.fromEntries(props.logAttributes), null, 2)}\n\`\`\`
            \n\n Help me understand this log message.
            `;
        }
        
        // Check if Guardian chat window exists
        const event = new CustomEvent('metoro:guardian:sendMessage', { 
            detail: { message }
        });
        window.dispatchEvent(event);
    };

    return (
        <div className="w-full grow h-[22px] justify-between items-center gap-4 inline-flex">
            <div className="flex items-center gap-4">
                <div className={"p-1 rounded justify-center items-center flex " + logLevelColor}>
                    <div className="text-textlight text-sm font-medium leading-[14px]">{props.logLevel.toUpperCase()}</div>
                </div>
                <div className="self-stretch justify-start items-center gap-2.5 flex">
                    <div className="whitespace-nowrap text-textmedium text-sm font-medium leading-[14px]">
                        {format(new Date(props.datetime), "PPPppp")}
                    </div>
                </div>
            </div>
            
            {guardianEnabled && (
                <button
                    onClick={handleSendToGuardian}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-textmedium bg-backgrounddark hover:bg-primary/10 hover:text-primary transition-colors"
                    title="Send to Guardian AI"
                >
                    <Bot className="h-3 w-3" />
                    <span>Ask Guardian</span>
                </button>
            )}
        </div>
    );
}

export function LogMessage(props: {
    colourBandOverride?: string,
    logLevel: string,
    logMessage: string,
    wrapLines: boolean
}) {
    let logLevelIndicatorColor = getLogLeveLIndicatorColor(props.logLevel)
    if (props.colourBandOverride !== undefined) {
        logLevelIndicatorColor = props.colourBandOverride
    }
    return <div className="w-full grow min-h-[15vh] max-h-64 bg-backgroundmedium rounded flex">
        {/* Color indicator that doesn't scroll */}
        <div className={"flex-shrink-0 w-[3px] " + logLevelIndicatorColor}/>
        
        {/* Content area that scrolls */}
        <div className={`flex-grow overflow-y-auto ${props.wrapLines ? 'overflow-x-hidden' : 'overflow-x-auto'}`}>
            <div className="w-full h-full p-4">
                <SyntaxHighlighter
                    className="w-full h-full"
                    wrapLongLines={props.wrapLines}
                    // @ts-ignore
                    style={customTheme2}
                    language={"text"}
                    customStyle={{
                        margin: 0,
                        padding: 0,
                        height: '100%',
                        width: props.wrapLines ? '100%' : 'auto',
                        minWidth: props.wrapLines ? '100%' : 'min-content',
                        overflow: 'visible',
                        backgroundColor: 'transparent',
                        whiteSpace: props.wrapLines ? 'pre-wrap' : 'pre',
                        wordBreak: props.wrapLines ? 'break-word' : 'normal',
                        overflowWrap: props.wrapLines ? 'break-word' : 'normal'
                    }}
                    >
                    {props.logMessage != undefined && props.logMessage != "" ? props.logMessage.replace("\\n", "\n\n") : props.logMessage}
                </SyntaxHighlighter>
            </div>
        </div>
    </div>
}

function LogMessageContainer(props: {
    overrideColourBand?: string,
    hideExcludeSearchButtons?: boolean,
    logLevel: string,
    logMessage: string,
    datetime: number,
    regexes: string[],
    excludeRegexes: string[],
    setRegexes: (regexes: string[]) => void,
    setExcludeRegexes: (regexes: string[]) => void,
    allLogAttributes?: Record<string, any>
    logAttributes?: Map<string, string>
}) {
    const [wrapLines, setWrapLines] = useState(true);
    let shouldShowAllLogAttributesAsBody = props.logMessage == ""
    let log = shouldShowAllLogAttributesAsBody && props.logAttributes ? JSON.stringify(Object.fromEntries(props.logAttributes)) : props.logMessage;
    let attributes = props.allLogAttributes;
    
    // Let's pretty print the log message if it's a JSON object
    try {
        const parsedLog = JSON.parse(props.logMessage);
        log = JSON.stringify(parsedLog, null, 2);
    } catch (e) {
        // Do nothing
    }

    const handleIncludeInSearch = () => {
        // If the log message is already in the regexes, don't add it again
        let searchElement = log;
        // Escape re2 special characters
        searchElement = searchElement.replace(/['-\/\\^$*+?.()|[\]{}]/g, '\\$&');

        if (props.regexes.includes(searchElement)) {
            return;
        }

        // If the log message is in the exclude regexes, remove it from there
        if (props.excludeRegexes.includes(searchElement)) {
            let regexes = [...props.excludeRegexes]
            regexes = regexes.filter((regex) => regex !== searchElement);
            props.setExcludeRegexes(regexes);
        }

        let regexes = [...props.regexes]
        regexes.push(searchElement);
        props.setRegexes(regexes);
    };

    const handleExcludeFromSearch = () => {
        // If the log message is already in the exclude regexes, don't add it again
        let searchElement = log;
        // Escape re2 special characters
        searchElement = searchElement.replace(/['-\/\\^$*+?.()|[\]{}]/g, '\\$&');

        if (props.excludeRegexes.includes(searchElement)) {
            return;
        }

        // If the log message is in the regexes, remove it from there
        if (props.regexes.includes(searchElement)) {
            let regexes = [...props.regexes]
            regexes = regexes.filter((regex) => regex !== searchElement);
            props.setRegexes(regexes);
        }
        let regexes = [...props.excludeRegexes]
        regexes.push(searchElement);
        props.setExcludeRegexes(regexes);
    };


    return (<div className="w-full flex-col justify-start items-start gap-4 inline-flex">
            <div className="w-full flex justify-between items-center">
                <div className="flex-col justify-start items-start flex">
                    <LogHeader 
                        logLevel={props.logLevel} 
                        datetime={props.datetime} 
                        logMessage={log}
                        logAttributes={attributes}
                    />
                </div>
                {!props.hideExcludeSearchButtons && 
                    <div className="flex gap-2 items-center">
                        <label className="flex items-center gap-2 text-textmedium cursor-pointer">
                            <input
                                type="checkbox"
                                checked={wrapLines}
                                onChange={(e) => setWrapLines(e.target.checked)}
                                className="form-checkbox h-4 w-4"
                            />
                            Wrap lines
                        </label>
                        {/*    Icons to include or exclude log lines*/}
                        <div
                            className="cursor-pointer text-textmedium bg-secondarytransparenter border-secondary border rounded p-1"
                            onClick={handleIncludeInSearch}>
                            Include in search
                        </div>
                        <div className="cursor-pointer text-textmedium bg-redtransparenter border-red-500 border rounded p-1"
                            onClick={handleExcludeFromSearch}>
                            Exclude from search
                        </div>
                    </div>
                }
            </div>
            {shouldShowAllLogAttributesAsBody && 
                <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded px-3 py-1.5 text-blue-400 mb-1">
                    <Info className="h-4 w-4" />
                    <span>No "msg", "body" or "message" found in the JSON log.  {attributes != undefined && "Showing full log body."}</span>
                </div>
            }
            <LogMessage
                colourBandOverride={props.overrideColourBand}
                logLevel={props.logLevel} 
                logMessage={log}
                wrapLines={wrapLines}
            />
        </div>
    )
}

export default LogMessageContainer;